export default defineNuxtPlugin(async () => {
  if (process.server || import.meta.server) {
    return;
  }

  const runtimeConfig = useRuntimeConfig();

  const HOTJAR_ID = runtimeConfig.public?.HOTJAR_ID || 182167;
  const HOTJAR_SV = runtimeConfig.public?.HOTJAR_SV || 6;

  const SCRIPT_ATTRIBUTE = 'data-hotjar-id';

  const ensureHotjarLoaded = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (window.hj) {
        return resolve();
      }

      // If script tag exists but hj not yet ready, poll briefly
      const existingScript = document.querySelector<HTMLScriptElement>(
        `script[${SCRIPT_ATTRIBUTE}="${HOTJAR_ID}"]`
      );
      if (existingScript) {
        const pollInterval = setInterval(() => {
          if (window.hj) {
            clearInterval(pollInterval);
            resolve();
          }
        }, 25);
        // Timeout safety
        setTimeout(() => {
          clearInterval(pollInterval);
          if (window.hj) {
            resolve();
          } else {
            reject(new Error('Hotjar failed to initialize within timeout.'));
          }
        }, 4000);
        return;
      }

      // Inject script
      window.hj =
        window.hj ||
        function () {
          (window.hj as any).q = (window.hj as any).q || [];
          // eslint-disable-next-line prefer-rest-params
          (window.hj as any).q.push(arguments);
        };
      (window as any)._hjSettings = { hjid: HOTJAR_ID, hjsv: HOTJAR_SV };

      const headElement = document.head;
      const scriptElement = document.createElement('script');
      scriptElement.async = true;
      scriptElement.defer = true;
      scriptElement.setAttribute(SCRIPT_ATTRIBUTE, String(HOTJAR_ID));
      scriptElement.src = `https://static.hotjar.com/c/hotjar-${HOTJAR_ID}.js?sv=${HOTJAR_SV}`;

      scriptElement.onload = () => resolve();
      scriptElement.onerror = () => reject(new Error('Failed to load Hotjar script'));
      headElement.appendChild(scriptElement);
    });
  };

  const triggerStateChange = (path: string) => {
    // Small delay allows DOM/body classes to settle before Hotjar runs targeting
    setTimeout(() => {
      if (window.hj) {
        window.hj('stateChange', path);
      }
    }, 40);
  };

  try {
    await ensureHotjarLoaded();
  } catch (error) {
    console.warn('[Hotjar] Initialization issue:', error);
    return;
  }

  const router = useRouter();
  triggerStateChange(router.currentRoute.value.fullPath);

  router.afterEach((to) => {
    triggerStateChange(to.fullPath);
  });
});
